import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ArrowLeft, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const InvestorDeckPDF = () => {
  const navigate = useNavigate();
  const deckRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="investor-deck-pdf">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden sticky top-0 z-50 bg-[#043D32] border-b border-white/10 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/investor')} className="text-white hover:text-white/80">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Presentation
          </Button>
          <div className="flex items-center gap-3">
            <Button onClick={handlePrint} className="bg-[#C6A962] hover:bg-[#B8985A] text-[#043D32]">
              <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
            </Button>
          </div>
        </div>
        <p className="text-center text-white/60 text-sm mt-2">
          Use your browser's Print function (Ctrl+P / Cmd+P) → Save as PDF
        </p>
      </div>

      {/* PDF Content */}
      <div ref={deckRef} className="max-w-4xl mx-auto">
        
        {/* Slide 1: Cover */}
        <div className="min-h-screen bg-[#043D32] p-16 flex flex-col justify-center items-center text-center print:break-after-page">
          <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-[#8B7230] via-[#C6A962] to-[#F5EBD3] flex items-center justify-center mb-8">
            <span className="text-[#043D32] text-5xl font-serif font-bold">R</span>
          </div>
          <h1 className="text-6xl font-serif text-[#C6A962] mb-4">RASHII</h1>
          <p className="text-xl text-white/80 mb-2">Decision Intelligence for Real Estate</p>
          <p className="text-white/60 mb-12">Part of the Subarashi Real Estate Ecosystem</p>
          <div className="inline-block px-6 py-2 bg-[#C6A962]/20 rounded-full">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">SEED INVESTOR DECK</span>
          </div>
          <p className="text-white/40 text-sm mt-16">Confidential | December 2025</p>
        </div>

        {/* Slide 2: The Problem */}
        <div className="min-h-screen bg-white p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">01 / THE PROBLEM</span>
          </div>
          <h2 className="text-5xl font-serif text-[#043D32] mb-12">Real Estate is Broken</h2>
          
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#043D32] mb-2">Information Asymmetry</h3>
                <p className="text-gray-600">Sellers know more than buyers. Agents gatekeep critical data. Buyers make million-dirham decisions without full information.</p>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#043D32] mb-2">Unverified Listings</h3>
                <p className="text-gray-600">Fake listings, inflated prices, and undisclosed defects plague the market. No standardized verification process.</p>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#043D32] mb-2">Fragmented Process</h3>
                <p className="text-gray-600">No single platform handles the full journey from search to settlement. Multiple handoffs, lost context, wasted time.</p>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold">4</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#043D32] mb-2">Emotional Decisions</h3>
                <p className="text-gray-600">Buyers make the biggest financial decisions of their lives based on gut feelings rather than data-driven insights.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Slide 3: The Solution */}
        <div className="min-h-screen bg-[#043D32] p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">02 / THE SOLUTION</span>
          </div>
          <h2 className="text-5xl font-serif text-white mb-12">Rashii Decision Intelligence</h2>
          
          <div className="grid grid-cols-2 gap-8">
            <div className="bg-white/10 rounded-lg p-6">
              <div className="w-10 h-10 rounded bg-[#C6A962]/20 flex items-center justify-center mb-4">
                <span className="text-[#C6A962]">✓</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Verified Exchange</h3>
              <p className="text-white/70">Every property, buyer, and seller is KYC-verified before participation.</p>
            </div>
            
            <div className="bg-white/10 rounded-lg p-6">
              <div className="w-10 h-10 rounded bg-[#C6A962]/20 flex items-center justify-center mb-4">
                <span className="text-[#C6A962]">★</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Rashii Score</h3>
              <p className="text-white/70">AI-powered scoring analyzes price fairness, location value, and market timing.</p>
            </div>
            
            <div className="bg-white/10 rounded-lg p-6">
              <div className="w-10 h-10 rounded bg-[#C6A962]/20 flex items-center justify-center mb-4">
                <span className="text-[#C6A962]">⚡</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Structured Negotiation</h3>
              <p className="text-white/70">Transparent offer/counter-offer system with deadline enforcement.</p>
            </div>
            
            <div className="bg-white/10 rounded-lg p-6">
              <div className="w-10 h-10 rounded bg-[#C6A962]/20 flex items-center justify-center mb-4">
                <span className="text-[#C6A962]">🔒</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Mandatory Settlement</h3>
              <p className="text-white/70">All transactions settle through Rashii escrow - ensuring completion.</p>
            </div>
          </div>
        </div>

        {/* Slide 4: Market Opportunity */}
        <div className="min-h-screen bg-white p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">03 / MARKET OPPORTUNITY</span>
          </div>
          <h2 className="text-5xl font-serif text-[#043D32] mb-12">The UAE Opportunity</h2>
          
          <div className="grid grid-cols-2 gap-8 mb-12">
            <div className="text-center p-8 bg-[#043D32] rounded-lg">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">AED 917B</div>
              <p className="text-white/80">Dubai Transactions (2025)</p>
              <p className="text-white/50 text-xs mt-1">Source: DLD</p>
            </div>
            <div className="text-center p-8 bg-[#043D32] rounded-lg">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">$1.55B</div>
              <p className="text-white/80">PropTech TAM by 2030</p>
              <p className="text-white/50 text-xs mt-1">Source: TechSci Research</p>
            </div>
            <div className="text-center p-8 bg-[#043D32] rounded-lg">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">270K+</div>
              <p className="text-white/80">Annual Transactions</p>
              <p className="text-white/50 text-xs mt-1">Source: DLD</p>
            </div>
            <div className="text-center p-8 bg-[#043D32] rounded-lg">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">45 Days</div>
              <p className="text-white/80">Average Transaction Time</p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold text-[#043D32] mb-4">Why Now?</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• Dubai's real estate market achieved record AED 917B in 2025 (DLD Official)</li>
              <li>• Government push for digital transformation (Dubai Real Estate Strategy 2033)</li>
              <li>• 193K+ investors in 2025, with 129K new investors entering the market</li>
              <li>• No dominant PropTech player - UAE market growing at 17.5% CAGR</li>
            </ul>
          </div>
        </div>

        {/* Slide 5: How It Works */}
        <div className="min-h-screen bg-[#043D32] p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">04 / HOW IT WORKS</span>
          </div>
          <h2 className="text-5xl font-serif text-white mb-12">The Rashii Journey</h2>
          
          <div className="mb-8">
            <h3 className="text-xl text-[#C6A962] mb-6">Buyer Journey</h3>
            <div className="flex justify-between">
              {['Register & KYC', 'Browse & Analyze', 'Commit Intent', 'Make Offer', 'Settle & Own'].map((step, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#C6A962] flex items-center justify-center mx-auto mb-2">
                    <span className="text-[#043D32] font-bold">{i + 1}</span>
                  </div>
                  <p className="text-white text-sm">{step}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-xl text-[#C6A962] mb-6">Seller Journey</h3>
            <div className="flex justify-between">
              {['Register & KYC', 'List Property', 'Get Rashii Score', 'Manage Viewings', 'Accept & Settle'].map((step, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                    <span className="text-white font-bold">{i + 1}</span>
                  </div>
                  <p className="text-white text-sm">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Slide 6: Business Model */}
        <div className="min-h-screen bg-white p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">05 / BUSINESS MODEL</span>
          </div>
          <h2 className="text-5xl font-serif text-[#043D32] mb-12">Revenue Streams</h2>
          
          <div className="grid grid-cols-2 gap-8">
            <div className="border-2 border-[#043D32] rounded-lg p-8 text-center">
              <div className="text-5xl font-serif text-[#043D32] mb-2">2-5%</div>
              <h3 className="text-xl font-semibold mb-2">Transaction Fee</h3>
              <p className="text-gray-600">2% (Secondary Market) | 5% (Off-Plan)</p>
            </div>
            
            <div className="border-2 border-[#043D32] rounded-lg p-8 text-center">
              <div className="text-5xl font-serif text-[#043D32] mb-2">AED 500</div>
              <h3 className="text-xl font-semibold mb-2">Listing Fee</h3>
              <p className="text-gray-600">Per property listing with Rashii Score analysis</p>
            </div>
            
            <div className="border-2 border-[#043D32] rounded-lg p-8 text-center">
              <div className="text-5xl font-serif text-[#043D32] mb-2">Premium</div>
              <h3 className="text-xl font-semibold mb-2">Intelligence Subscription</h3>
              <p className="text-gray-600">Advanced market analytics and investment tools</p>
            </div>
            
            <div className="border-2 border-[#043D32] rounded-lg p-8 text-center">
              <div className="text-5xl font-serif text-[#043D32] mb-2">Agent</div>
              <h3 className="text-xl font-semibold mb-2">Portal Fees</h3>
              <p className="text-gray-600">Licensed agent subscriptions and lead fees</p>
            </div>
          </div>
        </div>

        {/* Slide 7: The Ecosystem */}
        <div className="min-h-screen bg-[#043D32] p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">06 / THE ECOSYSTEM</span>
          </div>
          <h2 className="text-5xl font-serif text-white mb-12">Subarashi Ecosystem</h2>
          
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white/10 rounded-lg p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🏢</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Subarashi Real Estate</h3>
              <p className="text-white/70 text-sm mb-2">Traditional brokerage with verified agents</p>
              <p className="text-[#C6A962] text-sm mb-4">2-5% Sales | 5% Rental | 15% Holiday Homes</p>
              <span className="inline-block px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">ACTIVE</span>
            </div>
            
            <div className="bg-[#C6A962]/20 rounded-lg p-8 text-center border-2 border-[#C6A962]">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8B7230] via-[#C6A962] to-[#F5EBD3] flex items-center justify-center mx-auto mb-4">
                <span className="text-[#043D32] text-2xl font-serif font-bold">R</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Rashii Platform</h3>
              <p className="text-white/70 text-sm mb-2">Decision intelligence technology</p>
              <p className="text-[#C6A962] text-sm mb-4">2-5% Transaction | AED 500 Listing</p>
              <span className="inline-block px-3 py-1 bg-[#C6A962]/30 text-[#C6A962] text-xs rounded-full">BUILDING</span>
            </div>
            
            <div className="bg-blue-600/10 rounded-lg p-8 text-center border border-blue-500/30">
              <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💳</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Subaa</h3>
              <p className="text-white/70 text-sm mb-2">Rent payment flexibility platform</p>
              <p className="text-blue-400 text-sm mb-4">6% Financing | AED 500 Processing</p>
              <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">LAUNCHING</span>
            </div>
          </div>
        </div>

        {/* Slide 8: Roadmap */}
        <div className="min-h-screen bg-white p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">07 / ROADMAP</span>
          </div>
          <h2 className="text-5xl font-serif text-[#043D32] mb-12">Path to Market Leadership</h2>
          
          <div className="space-y-6">
            <div className="flex gap-6">
              <div className="w-24 flex-shrink-0">
                <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">Q1 2025</span>
              </div>
              <div className="flex-1 border-l-2 border-[#043D32] pl-6 pb-6">
                <h3 className="font-semibold text-[#043D32] mb-2">Platform Launch</h3>
                <p className="text-gray-600">Core buyer/seller journeys • KYC verification • Rashii Score v1 • Basic analytics</p>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="w-24 flex-shrink-0">
                <span className="inline-block px-3 py-1 bg-[#C6A962]/20 text-[#8B7230] text-sm rounded-full">Q2 2025</span>
              </div>
              <div className="flex-1 border-l-2 border-[#C6A962] pl-6 pb-6">
                <h3 className="font-semibold text-[#043D32] mb-2">Market Expansion</h3>
                <p className="text-gray-600">Agent portal launch • Escrow integration • UAE Pass login • Mobile app beta</p>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="w-24 flex-shrink-0">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">Q3-Q4 2025</span>
              </div>
              <div className="flex-1 border-l-2 border-gray-300 pl-6 pb-6">
                <h3 className="font-semibold text-[#043D32] mb-2">Intelligence Suite</h3>
                <p className="text-gray-600">Advanced analytics • DLD API integration • Predictive pricing • Investment tools</p>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="w-24 flex-shrink-0">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">2026</span>
              </div>
              <div className="flex-1 border-l-2 border-gray-300 pl-6">
                <h3 className="font-semibold text-[#043D32] mb-2">Scale & Expand</h3>
                <p className="text-gray-600">GCC market entry • Smart contracts • Tokenized records • Mortgage marketplace</p>
              </div>
            </div>
          </div>
        </div>

        {/* Slide 9: Investment Ask */}
        <div className="min-h-screen bg-[#043D32] p-16 print:break-after-page">
          <div className="mb-8">
            <span className="text-[#C6A962] text-sm font-medium tracking-wider">08 / THE ASK</span>
          </div>
          <h2 className="text-5xl font-serif text-white mb-12">Seed Round</h2>
          
          <div className="grid grid-cols-4 gap-6 mb-12">
            <div className="text-center">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">$5M</div>
              <p className="text-white/80">Target Raise</p>
              <p className="text-white/50 text-xs mt-1">(AED 18.4M)</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">$3M</div>
              <p className="text-white/80">Minimum Entry</p>
              <p className="text-white/50 text-xs mt-1">(AED 11M)</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">36 mo</div>
              <p className="text-white/80">Runway</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-serif text-[#C6A962] mb-2">Equity</div>
              <p className="text-white/80">Investment Type</p>
            </div>
          </div>
          
          <div className="bg-white/10 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-white mb-6">Use of Funds</h3>
            <div className="grid grid-cols-6 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-serif text-[#C6A962]">30%</div>
                <p className="text-white/80 text-xs">Marketing & Brand</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-serif text-[#C6A962]">25%</div>
                <p className="text-white/80 text-xs">Technology Dev</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-serif text-[#C6A962]">15%</div>
                <p className="text-white/80 text-xs">Operations</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-serif text-[#C6A962]">12%</div>
                <p className="text-white/80 text-xs">Banking Setup</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-serif text-[#C6A962]">10%</div>
                <p className="text-white/80 text-xs">RE Financing</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-serif text-[#C6A962]">8%</div>
                <p className="text-white/80 text-xs">Compliance</p>
              </div>
            </div>
            <div className="border-t border-white/20 pt-4">
              <ul className="text-white/70 text-sm space-y-1 grid grid-cols-2 gap-2">
                <li>• Marketing & Brand Awareness across UAE</li>
                <li>• Technology infrastructure & AI/ML development</li>
                <li>• Running costs & team expansion</li>
                <li>• Banking facilities & payment integrations</li>
                <li>• Real estate financing & holiday homes launch</li>
                <li>• Regulatory compliance & licensing</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Slide 10: Contact */}
        <div className="min-h-screen bg-white p-16 flex flex-col justify-center items-center text-center">
          <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-[#8B7230] via-[#C6A962] to-[#F5EBD3] flex items-center justify-center mb-8">
            <span className="text-[#043D32] text-4xl font-serif font-bold">R</span>
          </div>
          <h2 className="text-5xl font-serif text-[#043D32] mb-4">Let's Build Together</h2>
          <p className="text-gray-600 mb-8 max-w-lg">
            Join us in transforming real estate transactions in the UAE and beyond.
          </p>
          
          <div className="space-y-4 text-[#043D32]">
            <p className="text-xl font-medium">admin@subarashirealestate.com</p>
            <p>Dubai, United Arab Emirates</p>
          </div>
          
          <div className="mt-12 text-sm text-gray-400">
            <p>© 2025 Rashii | Part of Subarashi Ecosystem</p>
            <p className="mt-2">Confidential Investor Materials</p>
          </div>
        </div>

      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:break-after-page {
            break-after: page;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InvestorDeckPDF;
